import React, { useState, useEffect, useCallback, useRef, Suspense, lazy } from 'react';
import { Route, Navigate, Routes, useNavigate, useLocation } from 'react-router-dom';
import config from './config';
import { fetchLogout, fetchUserProfile } from './services/api';
import { markCurrentSessionInternal } from './services/analytics';
import { clearMessageDetailsCache } from './hooks/useMessageDetails';
import { authReturnTo, authRoute } from './utils/authNavigation';
import AppLoading from './components/Common/AppLoading';

const ChatContainer = lazy(() => import('./components/Chat/ChatContainer'));
const SharedThreadView = lazy(() => import('./components/Chat/SharedThreadView'));
const LoginForm = lazy(() => import('./components/LoginForm'));
const RegistrationForm = lazy(() => import('./components/RegistrationForm'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const AdminAnalyticsPage = lazy(() => import('./components/AdminAnalyticsPage'));
const ProjectsPage = lazy(() => import('./components/Projects/ProjectsPage'));
const CreateProjectPage = lazy(() => import('./components/Projects/CreateProjectPage'));
const ProjectWorkspacePage = lazy(() => import('./components/Projects/ProjectWorkspacePage'));
const PricingPage = lazy(() => import('./components/Pricing/PricingPage'));
const ActivationPage = lazy(() => import('./components/Pricing/ActivationPage'));
const CreditWalletPage = lazy(() => import('./components/Credits/CreditWalletPage'));
const PasswordReset = lazy(() => import('./components/PasswordReset'));
const PrivacyPolicy = lazy(() => import('./components/Legal/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./components/Legal/TermsOfService'));
const Profile = lazy(() => import('./components/Profile/Profile'));
// Keep the AgentPage loader shared by React.lazy and the route-specific
// preload below. Do not cache a rejected promise here: if a speculative chunk
// load fails, the normal lazy render must be able to call the loader again.
const loadAgentPage = () => import('./components/Agent/AgentPage');
const AgentPage = lazy(loadAgentPage);
// Marketing Pages (Signal theme)
const V9Home = lazy(() => import('./components/V9/V9Home'));
// Marketing Pages (Aurora theme)
const V10Home = lazy(() => import('./components/V10/V10Home'));
const FounderOnboardingForm = lazy(() => import('./components/FounderOnboardingForm'));

const getAuthScope = (payload) => {
  const user = payload?.user;
  if (typeof user === 'string' && user.trim()) return user.trim();
  if (user && typeof user === 'object') {
    const identity = user.user_id || user.id || user.username || user.email;
    if (typeof identity === 'string' && identity.trim()) return identity.trim();
    if (typeof identity === 'number' && Number.isFinite(identity)) return String(identity);
  }
  const fallback = payload?.user_id || payload?.username || payload?.email;
  return typeof fallback === 'string' && fallback.trim() ? fallback.trim() : null;
};

/** * Main App component handling routing and authentication */
const App = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  const [subscriptionTier, setSubscriptionTier] = useState('none');
  const [subscriptionStatus, setSubscriptionStatus] = useState('none');
  const [authScope, setAuthScope] = useState(null);
  const [authSessionKey, setAuthSessionKey] = useState(0);
  const authIdentity = authScope ? `${authScope}:${authSessionKey}` : null;
  const authSessionActiveRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  const getPostLoginPath = useCallback(() => {
    return authReturnTo({ search: location.search, state: location.state });
  }, [location.search, location.state]);

  // Initial auth check and periodic verification
  useEffect(() => {
    // AgentPage is the only route whose module is large enough to benefit
    // from speculative loading during its auth gate. Importing the module is
    // side-effect free; AgentPage mounts (and its socket hook starts) only
    // when the unchanged authenticated render gate below selects the route.
    if (location.pathname === '/agent') {
      void loadAgentPage().catch(() => {});
    }

    // Bypass auth checks only for pages that should never auto-redirect.  Keep
    // probing on `/` and `/login` so a valid remembered refresh cookie can be
    // validated by /check-auth and the user is sent straight back to the app.
    if (
      location.pathname === '/register' ||
      location.pathname === '/reset-password' ||
      location.pathname === '/privacy' ||
      location.pathname === '/terms'
    ) {
      setIsAuthChecked(true);
      return;
    }

    const checkAuthStatus = async () => {
      try {
        const response = await fetch(`${config.apiV2Url}/check-auth`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          if (!authSessionActiveRef.current) {
            clearMessageDetailsCache();
            setAuthSessionKey((value) => value + 1);
          }
          authSessionActiveRef.current = true;
          const authData = typeof response.clone === 'function'
            ? await response.clone().json().catch(() => ({}))
            : {};
          setIsLoggedIn(true);
          setAuthScope(getAuthScope(authData));
          if (authData.user === 'henosis') markCurrentSessionInternal();

          // Best-effort: load tier info from /profile (check-auth does not include tier).
          try {
            const profilePromise = fetchUserProfile();
            if (location.pathname === '/agent') {
              void import('./components/Common/MarkdownContent').catch(() => {});
            }
            const prof = await profilePromise;
            const tier = prof?.user?.subscription_tier || 'none';
            setSubscriptionTier(tier);
            setSubscriptionStatus(prof?.user?.subscription_status || 'none');
          } catch {
            setSubscriptionTier('none');
            setSubscriptionStatus('none');
          }

          // Remembered sessions should not leave the user on login/landing.
          if (window.location.pathname === '/login' || window.location.pathname === '/') {
            navigate(getPostLoginPath(), { replace: true });
          }
        } else {
          // If both checks fail, ensure we're logged out
          authSessionActiveRef.current = false;
          clearMessageDetailsCache();
          setIsLoggedIn(false);
          setAuthScope(null);
          setSubscriptionTier('none');
          setSubscriptionStatus('none');
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        authSessionActiveRef.current = false;
        clearMessageDetailsCache();
        setIsLoggedIn(false);
        setAuthScope(null);
        setSubscriptionTier('none');
        setSubscriptionStatus('none');
      } finally {
        setIsAuthChecked(true);
      }
    };

    checkAuthStatus();
    const authInterval = setInterval(checkAuthStatus, 1800000); // 30 minutes
    return () => clearInterval(authInterval);
  }, [getPostLoginPath, location.pathname, navigate]);

  const handleLogin = useCallback(async (loginData) => {
    try {
      clearMessageDetailsCache();
      authSessionActiveRef.current = true;
      setAuthSessionKey((value) => value + 1);
      setIsLoggedIn(true);
      setAuthScope(getAuthScope(loginData));
      setSubscriptionTier(loginData.subscriptionTier || 'none');
      setSubscriptionStatus(loginData.subscriptionStatus || 'none');

      // LoginForm stores device info only when remember_me is enabled.
      // We still keep this as a safe fallback in case a caller passes it.
      if (loginData?.device_id && loginData?.device_name) {
        localStorage.setItem('deviceId', loginData.device_id);
        localStorage.setItem('deviceName', loginData.device_name);
      }

      // Best-effort tier refresh from /profile (source of truth).
      try {
        const prof = await fetchUserProfile();
        const tier = prof?.user?.subscription_tier || loginData.subscriptionTier || 'none';
        setSubscriptionTier(tier);
        setSubscriptionStatus(prof?.user?.subscription_status || loginData.subscriptionStatus || 'none');
      } catch {
        // Keep whatever we already set.
      }
      navigate(getPostLoginPath(), { replace: true });
    } catch (error) {
      console.error('Login error:', error);
      alert('An error occurred during login. Please try again.');
    }
  }, [getPostLoginPath, navigate]);

  const handleRegister = useCallback(async (registrationData) => {
    try {
      clearMessageDetailsCache();
      authSessionActiveRef.current = true;
      setAuthSessionKey((value) => value + 1);
      setIsLoggedIn(true);
      setAuthScope(getAuthScope(registrationData));
      setSubscriptionTier('pending_subscription');
      setSubscriptionStatus('none');
      // Store device info for new registrations
      if (registrationData.device_id && registrationData.device_name) {
        localStorage.setItem('deviceId', registrationData.device_id);
        localStorage.setItem('deviceName', registrationData.device_name);
      }
      navigate('/chat', { replace: true });
    } catch (error) {
      console.error('Registration error:', error);
      alert('An error occurred during registration. Please try again.');
    }
  }, [navigate]);

  const handleLogout = useCallback(async () => {
    try {
      // Server-side: best-effort revoke device + clear cookies via FastAPI.
      await fetchLogout();

      // Client-side: clear auth state and any device info so the UI does not
      // immediately attempt device-based re-auth.
      authSessionActiveRef.current = false;
      setIsLoggedIn(false);
      setAuthScope(null);
      setSubscriptionTier('none');
      setSubscriptionStatus('none');
      localStorage.removeItem('deviceId');
      localStorage.removeItem('deviceName');
      clearMessageDetailsCache();
      sessionStorage.removeItem('henosis.selectedModel');
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      alert('An error occurred during logout. Please try again.');
    }
  }, [navigate]);

  if (!isAuthChecked) {
    return <AppLoading />;
  }

  return (
    <Suspense fallback={<AppLoading />}>
      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={
            isLoggedIn ? (
              <Navigate to={getPostLoginPath()} replace />
            ) : (
              <LoginForm
                onLogin={handleLogin}
                onSwitchToRegister={() => navigate(authRoute('/register', location))}
              />
            )
          }
        />
        <Route
          path="/register"
          element={
            isLoggedIn ? (
              <Navigate to="/chat" replace />
            ) : (
              <RegistrationForm
                onRegister={handleRegister}
                onSwitchToLogin={() => navigate(authRoute('/login', location))}
              />
            )
          }
        />
        <Route
          path="/reset-password"
          element={
            isLoggedIn ? (
              <Navigate to="/chat" replace />
            ) : (
              <PasswordReset
                onLogin={handleLogin}
                onSwitchToRegister={() => navigate(authRoute('/register', location))}
              />
            )
          }
        />
        <Route
          path="/privacy"
          element={<PrivacyPolicy />}
        />
        <Route
          path="/terms"
          element={<TermsOfService />}
        />
        <Route path="/founder-onboarding" element={<FounderOnboardingForm />} />
        {/* Pricing Routes - Accessible to both logged in and non-logged in users */}
        <Route
          path="/pricing"
          element={
            <PricingPage
              currentTier={subscriptionTier}
              isLoggedIn={isLoggedIn}
              onUpgrade={(tier) => {
                if (!isLoggedIn) {
                  navigate('/login');
                }
                // Handle upgrade logic
              }}
            />
          }
        />
        <Route
          path="/activate"
          element={
            isLoggedIn ? (
              <ActivationPage
                subscriptionTier={subscriptionTier}
                subscriptionStatus={subscriptionStatus}
              />
            ) : (
              <Navigate to="/login?returnTo=/activate" replace />
            )
          }
        />
        <Route
          path="/credits"
          element={
            isLoggedIn ? (
              <CreditWalletPage />
            ) : (
              <Navigate to="/login?returnTo=/credits" replace />
            )
          }
        />
        {/* Protected Routes */}
        <Route
          path="/profile"
          element={
            isLoggedIn ? (
              <Profile
                onLogout={handleLogout}
                subscriptionTier={subscriptionTier}
              />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/agent"
          element={
            isLoggedIn ? (
              <AgentPage onLogout={handleLogout} authScope={authScope} authIdentity={authIdentity} />
            ) : (
              <Navigate to="/login?returnTo=/agent" replace />
            )
          }
        />
        <Route
          path="/chat"
          element={
            isLoggedIn ? (
              <ChatContainer
                onLogout={handleLogout}
                subscriptionTier={subscriptionTier}
                authIdentity={authIdentity}
                authScope={authScope}
                isLanding={true}
              />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/chat/:threadId"
          element={
            isLoggedIn ? (
              <ChatContainer
                onLogout={handleLogout}
                subscriptionTier={subscriptionTier}
                authIdentity={authIdentity}
                authScope={authScope}
                isLanding={false}
              />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/dashboard"
          element={
            isLoggedIn ? (
              <Dashboard
                onLogout={handleLogout}
                subscriptionTier={subscriptionTier}
              />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/admin/analytics"
          element={
            isLoggedIn ? (
              <AdminAnalyticsPage onLogout={handleLogout} />
            ) : (
              <Navigate to="/login?returnTo=/admin/analytics" replace />
            )
          }
        />
        <Route
          path="/projects"
          element={
            isLoggedIn ? (
              <ProjectsPage
                onLogout={handleLogout}
                subscriptionTier={subscriptionTier}
              />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/projects/new"
          element={
            isLoggedIn ? (
              <CreateProjectPage subscriptionTier={subscriptionTier} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/projects/:projectId"
          element={
            isLoggedIn ? (
              <ProjectWorkspacePage />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/shared/:threadId"
          element={
            <SharedThreadView
              subscriptionTier={subscriptionTier}
              isLoggedIn={isLoggedIn}
            />
          }
        />
        {/* Root and Catch-All Routes */}
        {/* Default to V9 landing for non-logged-in visitors */}
        <Route path="/" element={ isLoggedIn ? (<Navigate to="/chat" replace />) : (<V9Home />) } />
        {/* Redesigned V10 landing, kept for reference */}
        <Route path="/v10/landing" element={<V10Home />} />
        <Route
          path="*"
          element={<Navigate to={isLoggedIn ? "/chat" : "/login"} replace />}
        />
      </Routes>
    </Suspense>
  );
};

export default App;
